import { useEffect, useState } from "react";
import {
  Typography,
  Card as MuiCard,
  CardContent,
  Divider,
  Grid,
  TextField as MuiTextField,
  Button,
  Box,
  CircularProgress,
  Paper as MuiPaper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { spacing } from "@mui/system";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import useAppDispatch from "../../hooks/useAppDispatch";
import {
  fetchSubscriptionsByAssociation,
  updateSubscription,
  SubscriptionStatus,
} from "../../redux/slices/subscription";
import useAuth from "../../hooks/useAuth";
import styled from "@emotion/styled";
import useAppSelector from "../../hooks/useAppSelector";
import useSnackbar from "./SnackbarHook";
import CustomSnackbar from "./SnackBar";
import PlanModal from "./PlanModal";
import {
  createSubscriptionInvoice,
  fetchSubscriptionInvoices,
} from "../../redux/slices/subscriptionInvoice";
import PDFViewerModal from "../../pages/PDFViewer";
import { baseUrl } from "../../config";
import { fetchFiles } from "../../redux/slices/file";
import { Download, Search } from "@mui/icons-material";
import { fetchAllPlans } from "../../redux/slices/plan";
import {
  useEffectivePermissions,
  checkPermission,
} from "../../hooks/usePermission";

const TextField = styled(MuiTextField)(spacing);
const Paper = styled(MuiPaper)(spacing);
const Card = styled(MuiCard)(spacing);

interface Props {
  invoices: any[];
  isReady: boolean;
  getInvoiceStatusLabel: (status: string) => string;
  handleShowInvoiceModal: (invoice: any, isOpen: boolean) => void;
}

const AssociationInvoiceTable = ({
  invoices,
  isReady,
  getInvoiceStatusLabel,
  handleShowInvoiceModal,
}: Props) => {
  const handleDownload = async (rowFile: any) => {
    const filename = rowFile.pdf?.filename;
    try {
      const token = localStorage.getItem("accessToken");

      const response = await fetch(`${baseUrl}/files/download/${filename}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      link?.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading the file:", error);
    }
  };
  return (
    <Paper>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Laskun numero</TableCell>

              <TableCell>Eräpäivä</TableCell>
              <TableCell>Summa (€)</TableCell>
              <TableCell>Tila</TableCell>
              <TableCell>Toiminnot</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!isReady ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  Ei laskuja
                </TableCell>
              </TableRow>
            ) : (
              invoices.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.number}</TableCell>

                  <TableCell>
                    {row.dueAt && !isNaN(new Date(row.dueAt).getTime())
                      ? new Date(row.dueAt).toLocaleDateString()
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {typeof row.amountCents === "number"
                      ? (row.amountCents / 100).toFixed(2)
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getInvoiceStatusLabel(row.status)}
                      color={
                        row.status === "paid"
                          ? "success"
                          : row.status === "open"
                          ? "primary"
                          : "default"
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Lataa">
                      <IconButton
                        size="small"
                        onClick={() => handleDownload(row)}
                      >
                        <Download fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Näytä tiedot">
                      <IconButton
                        size="small"
                        onClick={() => handleShowInvoiceModal(row, true)}
                      >
                        <Search fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

const AssociationInvoice = () => {
  //const { associationId } = useParams();
  const dispatch = useAppDispatch();
  const { currentUser } = useAuth();
  const { subscriptions, status } = useAppSelector(
    (state) => state.subscription
  );
  const { plans } = useAppSelector((state) => state.plan);

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  // RBAC permission check
  const { permissions } = useEffectivePermissions(
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id
  );

  const canUpdateSubscription = checkPermission(
    permissions,
    "subscription",
    "update"
  );

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfOpen, setPdfOpen] = useState(false);

  const [invoices, setInvoices] = useState<any[]>([]);
  const [isReady, setIsReady] = useState(false);

  const handleShowInvoiceModal = (invoice: any, isOpen: boolean) => {
    const filename = invoice.pdf?.filename;

    if (!filename) {
      setSnackbarMessage("Laskutiedostoa ei löytynyt.");
      setSnackbarSeverity("warning");
      setOpenSnackbar(true);
      return;
    }

    setPdfUrl(`${baseUrl}/files/preview/${filename}`);
    setPdfOpen(isOpen);
  };

  useEffect(() => {
    dispatch(fetchAllPlans());
  }, [dispatch]);

  useEffect(() => {
    if (!associationId) {
      return;
    }
    dispatch(fetchSubscriptionsByAssociation(associationId));
  }, [dispatch, associationId]);

  useEffect(() => {
    if (associationId) {
      dispatch(fetchSubscriptionInvoices(associationId))
        .unwrap()
        .then((data) => {
          setInvoices(data);
          setIsReady(true);
        })
        .catch(() => {
          setIsReady(true);
        });
    }
  }, [dispatch, associationId]);

  useEffect(() => {
    if (associationId) {
      dispatch(fetchFiles(parseInt(associationId)));
    }
  }, [dispatch, associationId]);

  const getInvoiceStatusLabel = (status: string) => {
    switch (status) {
      case "open":
        return "Avoin";
      case "paid":
        return "Maksettu";
      case "void":
        return "Hävitetty";
      default:
        return "Tuntematon";
    }
  };

  const translateStatus = (status: string): string => {
    switch (status) {
      case "trialing":
        return "Kokeilu";
      case "active":
        return "Aktiivinen";
      case "canceled":
        return "Peruutettu";
      case "expired":
        return "Päättynyt";
      default:
        return "Tuntematon";
    }
  };

  const {
    openSnackbar,
    snackbarMessage,
    snackbarSeverity,
    progress,
    setOpenSnackbar,
    setSnackbarMessage,
    setSnackbarSeverity,
    setProgress,
    duration,
  } = useSnackbar();

  const [initialValues, setInitialValues] = useState({
    name: currentUser?.activeAssociation?.name ?? "",
    municipality: currentUser?.activeAssociation?.road?.municipality ?? "",
    subscription: {
      id: 0,
      status: "" as SubscriptionStatus,
      createdAt: "",
      updatedAt: "",
      currentPeriodEnd: "",
      associationId: 0,
      planId: 0,
      planName: "",
    },
  });

  const [openPlanModal, setOpenPlanModal] = useState(false);

  const handleOpenPlanModal = () => setOpenPlanModal(true);
  const handleClosePlanModal = () => setOpenPlanModal(false);

  const handlePlanUpdate = async (newPlanId: string) => {
    if (!initialValues.subscription.id || !associationId) return;

    try {
      const now = new Date();
      const end = new Date(now);
      end.setFullYear(end.getFullYear() + 1);

      await dispatch(
        updateSubscription(initialValues.subscription.id, {
          associationId: parseInt(associationId, 10),
          planId: parseInt(newPlanId, 10),
          status: SubscriptionStatus.ACTIVE,
          trialStart: null,
          trialEnd: null,
          currentPeriodStart: now.toISOString(),
          currentPeriodEnd: end.toISOString(),
        })
      );
      const selectedPlan = plans.find(
        (plan) => plan.id.toString() === newPlanId
      );

      if (selectedPlan) {
        const dueEnd = new Date(now);
        dueEnd.setDate(now.getDate() + 7);
        const newInvoiceData = {
          number: `INV-${Date.now()}`,
          issuedAt: now.toISOString(),
          dueAt: dueEnd.toISOString(),
          amountCents: selectedPlan.priceCents,
          taxCents: Math.round(
            (selectedPlan.priceCents - selectedPlan.priceCents * 0.745) * 12
          ),
          periodEnd: end.toISOString(),
          status: "generating",
          subscriptionId: initialValues.subscription.id,
        };

        await dispatch(
          createSubscriptionInvoice(newInvoiceData, parseInt(associationId))
        );
        const updatedInvoices = await dispatch(
          fetchSubscriptionInvoices(parseInt(associationId))
        ).unwrap();
        setInvoices(updatedInvoices);
      }

      setProgress(100);
      setSnackbarMessage(`Tilaus päivitetty paketiksi: ${selectedPlan?.name}`);
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
    } catch (error) {
      setProgress(100);
      setSnackbarMessage("Tilauksen päivitys epäonnistui.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  const handleDeactivateSubscription = async () => {
    if (!initialValues.subscription.id || !associationId) return;

    try {
      await dispatch(
        updateSubscription(initialValues.subscription.id, {
          associationId: parseInt(associationId, 10),
          planId: subscriptions[0]?.plan?.id?.toString() || "",
          status: SubscriptionStatus.CANCELED,
          trialStart: null,
          trialEnd: null,
          currentPeriodStart: new Date().toISOString(),
          currentPeriodEnd: null,
        })
      );

      setProgress(100);
      setSnackbarMessage("Tilaus on nyt peruutettu.");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
    } catch (error) {
      setProgress(100);
      setSnackbarMessage("Tilauksen peruuttaminen epäonnistui.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  useEffect(() => {
    if (subscriptions && subscriptions.length > 0) {
      const firstSubscription = subscriptions[0];
      if (associationId) {
        setInitialValues({
          name: currentUser?.activeAssociation?.name ?? "",
          municipality:
            currentUser?.activeAssociation?.road?.municipality ?? "",
          subscription: {
            id: firstSubscription.id,
            status: firstSubscription.status,
            createdAt: firstSubscription.createdAt
              ? firstSubscription.createdAt.split("T")[0]
              : "",
            updatedAt: firstSubscription.updatedAt
              ? firstSubscription.updatedAt.split("T")[0]
              : "",
            currentPeriodEnd: firstSubscription.currentPeriodEnd
              ? firstSubscription.currentPeriodEnd.split("T")[0]
              : "",
            associationId: parseInt(associationId),
            planId: firstSubscription.planId,
            planName: firstSubscription.plan?.name ?? "",
          },
        });
      }
    }
  }, [subscriptions, currentUser, associationId]);

  const availablePlans = plans.map((plan) => ({
    id: plan.id.toString(),
    name: plan.name,
    price: plan.priceCents,
    currency: plan.currency,
    period: plan.interval,
    entitlements: plan.metadata?.entitlements ?? {},
  }));

  if (!currentUser || status === "loading") {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Helmet title="Tilaus" />
      <Typography variant="h3">Tilaus</Typography>
      <Divider sx={{ my: 4 }} />
      <Box component="form">
        {!canUpdateSubscription && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Huom: Et voi muokata jäsenyyden tietoja ilman ylläpitäjän oikeuksia.
          </Typography>
        )}

        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6">Tiekunnan perustiedot</Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={6} sx={{ my: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  name="name"
                  label="Tien nimi"
                  fullWidth
                  value={initialValues.name}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="municipality"
                  label="Kunta"
                  fullWidth
                  value={initialValues.municipality}
                  disabled
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6">Tilaus</Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={6} sx={{ my: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  name="subscription.planName"
                  label="Nykyinen paketti"
                  fullWidth
                  value={initialValues.subscription.planName}
                  InputProps={{ readOnly: true }}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="subscription.status"
                  label="Tilauksen tila"
                  fullWidth
                  value={translateStatus(initialValues.subscription.status)}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="subscription.createdAt"
                  label="Luotu"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={
                    initialValues.subscription.createdAt
                      ? new Date(initialValues.subscription.createdAt)
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  disabled
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  name="subscription.currentPeriodEnd"
                  label="Voimassa asti"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={
                    initialValues.subscription.currentPeriodEnd
                      ? new Date(initialValues.subscription.currentPeriodEnd)
                          .toISOString()
                          .split("T")[0]
                      : subscriptions?.[0]?.trialEnd
                      ? new Date(subscriptions[0].trialEnd)
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  disabled
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 4, display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleOpenPlanModal}
                disabled={!canUpdateSubscription}
              >
                {subscriptions &&
                subscriptions.length > 0 &&
                subscriptions[0]?.status === "active"
                  ? "Muokkaa tilausta"
                  : "Lisää tilaus"}
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6">Laskut</Typography>
            <Divider sx={{ my: 2 }} />
            <AssociationInvoiceTable
              invoices={invoices}
              isReady={isReady}
              getInvoiceStatusLabel={getInvoiceStatusLabel}
              handleShowInvoiceModal={handleShowInvoiceModal}
            />
          </CardContent>
        </Card>
      </Box>
      <CustomSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        progress={progress}
        duration={duration}
        onClose={() => setOpenSnackbar(false)}
      />
      <PDFViewerModal
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        fileUrl={pdfUrl ?? ""}
        authToken={localStorage.getItem("accessToken")}
      />
      <PlanModal
        open={openPlanModal}
        onClose={handleClosePlanModal}
        onConfirm={handlePlanUpdate}
        currentPlanId={subscriptions[0]?.plan?.id?.toString()}
        onDeactivate={handleDeactivateSubscription}
        plans={availablePlans}
      />
    </>
  );
};

export default AssociationInvoice;
